import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const sanitizeFileName = (name) => {
  return (name || "saved_results")
    .replace(/[^a-zA-Z0-9_\- ]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .toLowerCase();
};

const stripFts = (str) => {
  if (typeof str !== "string") return str || "";
  return str.replace(/<\/?(?:fts_match|span)[^>]*>/gi, "");
};

export const exportSavedResultsCSV = (collection) => {
  if (!collection || !collection.items || !collection.items.length) return;

  const entityType = (collection.entity_type || collection.entityType || "items").toLowerCase();
  let headers = [];
  let rows = [];

  if (entityType.includes("researcher") || entityType.includes("faculty")) {
    headers = ["Name", "Position", "Institution", "H-Index", "Citations", "Publications", "Keywords"];
    rows = collection.items.map((item) => [
      `"${stripFts(item.name || "").replace(/"/g, '""')}"`,
      `"${stripFts(item.position || "").replace(/"/g, '""')}"`,
      `"${stripFts(item.institution_name || item.institution || "").replace(/"/g, '""')}"`,
      item.h_index ?? item.hIndex ?? "",
      item.citation_count ?? item.citations ?? "",
      item.publication_count ?? item.publications ?? "",
      `"${stripFts(Array.isArray(item.keywords) ? item.keywords.join(", ") : (item.keywords || "")).replace(/"/g, '""')}"`,
    ]);
  } else if (entityType.includes("unit")) {
    headers = ["Unit Name", "Institution", "Faculties", "Total Publications", "Total Citations", "Total H-Index", "Keywords"];
    rows = collection.items.map((item) => [
      `"${stripFts(item.name || "").replace(/"/g, '""')}"`,
      `"${stripFts(item.institution_name || "").replace(/"/g, '""')}"`,
      item.total_faculties ?? item.faculties ?? "",
      item.total_publication ?? item.publications ?? "",
      item.total_citation ?? item.citations ?? "",
      item.total_h_index ?? item.hIndex ?? "",
      `"${stripFts(Array.isArray(item.keywords) ? item.keywords.join(", ") : (item.keywords || "")).replace(/"/g, '""')}"`,
    ]);
  } else if (entityType.includes("venue")) {
    headers = ["Venue Name", "H-Index", "Citations", "Publications", "Keywords"];
    rows = collection.items.map((item) => [
      `"${stripFts(item.name || "").replace(/"/g, '""')}"`,
      item.h_index ?? item.hIndex ?? "",
      item.citation_count ?? item.citations ?? "",
      item.publication_count ?? item.publications ?? "",
      `"${stripFts(Array.isArray(item.keywords) ? item.keywords.join(", ") : (item.keywords || "")).replace(/"/g, '""')}"`,
    ]);
  } else {
    // Institutions
    headers = ["Institution Name", "City", "State", "Total Faculties", "Total Publications", "Total Citations", "Total H-Index"];
    rows = collection.items.map((item) => [
      `"${stripFts(item.name || "").replace(/"/g, '""')}"`,
      `"${stripFts(item.city || "").replace(/"/g, '""')}"`,
      `"${stripFts(item.state || "").replace(/"/g, '""')}"`,
      item.total_faculties ?? item.faculties ?? "",
      item.total_publication ?? item.publications ?? "",
      item.total_citation ?? item.citations ?? "",
      item.total_h_index ?? item.hIndex ?? "",
    ]);
  }

  const csvContent = "\uFEFF" + [headers.join(","), ...rows.map((r) => r.join(","))].join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `${sanitizeFileName(collection.name)}.csv`);
};

export const exportSavedResultsXLSX = async (collection) => {
  if (!collection || !collection.items || !collection.items.length) return;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Academic Online Explore";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet("Saved Results");

  const THEME_COLOR = "1e40af"; // primary blue
  const HEADER_FILL = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: THEME_COLOR },
  };
  const HEADER_FONT = { color: { argb: "FFFFFFFF" }, bold: true, size: 11 };
  const TITLE_FONT = { bold: true, size: 15, color: { argb: THEME_COLOR } };
  const BORDER_STYLE = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } },
  };

  // Title Row
  worksheet.mergeCells("A1:G1");
  const titleCell = worksheet.getCell("A1");
  titleCell.value = collection.name;
  titleCell.font = TITLE_FONT;
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  worksheet.getRow(1).height = 32;

  // Metadata Row
  worksheet.mergeCells("A2:G2");
  const metaCell = worksheet.getCell("A2");
  const entityLabel = collection.entity_type || collection.entityType || "General";
  const savedDateStr = new Date(collection.created_at || collection.createdAt || Date.now()).toLocaleDateString();
  metaCell.value = `Entity: ${entityLabel} | Total Items: ${collection.items.length} | Saved Date: ${savedDateStr}`;
  metaCell.font = { italic: true, size: 10, color: { argb: "FF6B7280" } };
  worksheet.getRow(2).height = 20;

  worksheet.addRow([]); // Blank line

  const entityType = entityLabel.toLowerCase();
  let columns = [];
  let dataRows = [];

  if (entityType.includes("researcher") || entityType.includes("faculty")) {
    columns = [
      { header: "Name", key: "name", width: 28 },
      { header: "Position", key: "position", width: 22 },
      { header: "Institution", key: "institution", width: 32 },
      { header: "H-Index", key: "hIndex", width: 12 },
      { header: "Citations", key: "citations", width: 14 },
      { header: "Publications", key: "publications", width: 16 },
      { header: "Keywords", key: "keywords", width: 45 },
    ];
    dataRows = collection.items.map((item) => ({
      name: stripFts(item.name || ""),
      position: stripFts(item.position || ""),
      institution: stripFts(item.institution_name || item.institution || ""),
      hIndex: item.h_index ?? item.hIndex ?? 0,
      citations: item.citation_count ?? item.citations ?? 0,
      publications: item.publication_count ?? item.publications ?? 0,
      keywords: stripFts(Array.isArray(item.keywords) ? item.keywords.join(", ") : (item.keywords || "")),
    }));
  } else if (entityType.includes("unit")) {
    columns = [
      { header: "Unit Name", key: "name", width: 35 },
      { header: "Institution", key: "institution", width: 32 },
      { header: "Faculties", key: "faculties", width: 14 },
      { header: "Total Publications", key: "publications", width: 18 },
      { header: "Total Citations", key: "citations", width: 16 },
      { header: "Total H-Index", key: "hIndex", width: 14 },
      { header: "Keywords", key: "keywords", width: 45 },
    ];
    dataRows = collection.items.map((item) => ({
      name: stripFts(item.name || ""),
      institution: stripFts(item.institution_name || ""),
      faculties: item.total_faculties ?? item.faculties ?? 0,
      publications: item.total_publication ?? item.publications ?? 0,
      citations: item.total_citation ?? item.citations ?? 0,
      hIndex: item.total_h_index ?? item.hIndex ?? 0,
      keywords: stripFts(Array.isArray(item.keywords) ? item.keywords.join(", ") : (item.keywords || "")),
    }));
  } else if (entityType.includes("venue")) {
    columns = [
      { header: "Venue Name", key: "name", width: 35 },
      { header: "H-Index", key: "hIndex", width: 14 },
      { header: "Citations", key: "citations", width: 16 },
      { header: "Publications", key: "publications", width: 18 },
      { header: "Keywords", key: "keywords", width: 45 },
    ];
    dataRows = collection.items.map((item) => ({
      name: stripFts(item.name || ""),
      hIndex: item.h_index ?? item.hIndex ?? 0,
      citations: item.citation_count ?? item.citations ?? 0,
      publications: item.publication_count ?? item.publications ?? 0,
      keywords: stripFts(Array.isArray(item.keywords) ? item.keywords.join(", ") : (item.keywords || "")),
    }));
  } else {
    // Institutions
    columns = [
      { header: "Institution Name", key: "name", width: 35 },
      { header: "City", key: "city", width: 20 },
      { header: "State", key: "state", width: 12 },
      { header: "Total Faculties", key: "faculties", width: 16 },
      { header: "Total Publications", key: "publications", width: 18 },
      { header: "Total Citations", key: "citations", width: 16 },
      { header: "Total H-Index", key: "hIndex", width: 14 },
    ];
    dataRows = collection.items.map((item) => ({
      name: stripFts(item.name || ""),
      city: stripFts(item.city || ""),
      state: stripFts(item.state || ""),
      faculties: item.total_faculties ?? item.faculties ?? 0,
      publications: item.total_publication ?? item.publications ?? 0,
      citations: item.total_citation ?? item.citations ?? 0,
      hIndex: item.total_h_index ?? item.hIndex ?? 0,
    }));
  }

  // Header row at row 4
  const headerRow = worksheet.getRow(4);
  headerRow.height = 26;
  columns.forEach((col, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = col.header;
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = { vertical: "middle", horizontal: "center" };
    worksheet.getColumn(idx + 1).width = col.width;
  });

  // Data rows
  dataRows.forEach((rowObj, rIdx) => {
    const row = worksheet.getRow(5 + rIdx);
    row.height = 22;
    columns.forEach((col, cIdx) => {
      const cell = row.getCell(cIdx + 1);
      cell.value = rowObj[col.key];
      cell.border = BORDER_STYLE;
      if (typeof rowObj[col.key] === "number") {
        cell.alignment = { vertical: "middle", horizontal: "right" };
        cell.numFmt = "#,##0";
      } else {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      }
      if (rIdx % 2 === 1) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF9FAFB" },
        };
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${sanitizeFileName(collection.name)}.xlsx`);
};
